// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { Checkbox, FontIcon, IconButton } from "@fluentui/react";
import _ from "lodash";
import React, { Fragment, MouseEvent } from "react";
import { strings } from "../../../../common/strings";
import { getPrimaryGreyTheme } from "../../../../common/themes";
import {
    FieldFormat,
    FieldType,
    ILabel,
    ITableLabel,
    ITag,
    TagInputMode,
} from "../../../../models/applicationState";
import { tagIndexKeys } from "./tagIndexKeys";
import TagInputItemLabel from "./tagInputItemLabel";

export interface ITagClickProps {
    ctrlKey?: boolean;
    altKey?: boolean;
    keyClick?: boolean;
    clickedColor?: boolean;
    clickedDropDown?: boolean;
}

/**
 * Properties for tag input item
 */
export interface ITagInputItemProps {
    /** Tag represented by item */
    tag: ITag;
    /** Index of tag within tags array */
    index: number;
    /** Labels owned by the tag */
    labels: ILabel[];
    /** show Origin Labels or not */
    showOriginLabels: boolean;
    /** Tag is currently renaming */
    isRenaming: boolean;
    /** Tag is currently locked for application */
    isLocked: boolean;
    /** Tag is currently selected */
    isSelected: boolean;
    /** Tag is currently applied to one of the selected regions */
    appliedToSelectedRegions: boolean;
    /** Function to call upon clicking item */
    onClick: (tag: ITag, props: ITagClickProps) => void;
    /** Apply new name to tag */
    onRename: (
        oldTag: ITag,
        newName: string,
        cancelCallback: () => void
    ) => void;
    onLabelEnter: (label: ILabel | ITableLabel) => void;
    onLabelClick: (label: ILabel | ITableLabel) => void;
    onLabelLeave: (label: ILabel) => void;
    onTagChanged?: (oldTag: ITag, newTag: ITag) => void;
    handleLabelTable: (
        tagInputMode: TagInputMode,
        selectedTableTagToLabel
    ) => void;
    addRowToDynamicTable: () => void;
    onTagDoubleClick?: (label: ILabel) => void;
    onDeleteCheckedChanged: (name: string, value: boolean) => void;
    onLabelEdit: (label: ILabel) => void;
    onResetClick: (label: ILabel) => void;
    selectAllForDelete?: boolean;
    widgetMode?: boolean;
    isV3?: boolean;
    splitPaneWidth?: number;
    status?: string;
}

export interface ITagInputItemState {
    /** Tag is currently renaming */
    isRenaming: boolean;

    /** Tag is currently locked for application */
    isLocked: boolean;
    selectAllForDelete: boolean;
    isTagNameOverflowing: boolean;
}

export default class TagInputItem extends React.Component<
    ITagInputItemProps,
    ITagInputItemState
> {
    private tagNameRef: React.RefObject<HTMLSpanElement>;
    private tagConfidenceRef: React.RefObject<HTMLSpanElement>;
    private tagBodyRef: React.RefObject<HTMLDivElement>;
    // public state: ITagInputItemState = {
    //     isRenaming: false,
    //     isLocked: false,
    //     selectAllForDelete: false,
    // };

    constructor(props) {
        super(props);
        this.tagNameRef = React.createRef(); // Create a ref for the tag name element
        this.tagConfidenceRef = React.createRef(); // Create a ref for the tag confidence element
        this.tagBodyRef = React.createRef(); // Create a ref for the tag body element
        this.state = {
            isRenaming: false,
            isLocked: false,
            selectAllForDelete: false,
            isTagNameOverflowing: false, // Track whether the tag name is overflowing
        };
    }

    private itemRef = React.createRef<HTMLDivElement>();
    private inputElement: HTMLInputElement;

    componentDidMount() {
        this.checkTagNameOverflow(); // Check for overflow after the component mounts
    }

    public componentDidUpdate(prevProps: ITagInputItemProps) {
        if (prevProps.isRenaming !== this.props.isRenaming) {
            this.setState(
                {
                    isRenaming: this.props.isRenaming,
                },
                () => {
                    if (this.props.isRenaming) {
                        this.inputElement.focus();
                    }
                }
            );
        }

        if (prevProps.isLocked !== this.props.isLocked) {
            this.setState({
                isLocked: this.props.isLocked,
            });
        }

        if (prevProps.splitPaneWidth !== this.props.splitPaneWidth) {
            this.checkTagNameOverflow(); // Recheck for overflow if the tag name changes
        }
    }

    checkTagNameOverflow = () => {
        if (this.tagNameRef.current) {
            const element = this.tagNameRef.current;
            const availableWidth = this.props.splitPaneWidth - 50; // Subtract padding or margins if necessary
            const isOverflowing = element.scrollWidth > availableWidth; // Check if the content overflows
            this.setState({ isTagNameOverflowing: isOverflowing });
        }
    };

    public render() {
        const style: any = {
            background: this.props.tag.color,
        };
        return (
            <div className={"tag-item-block"}>
                <div
                    className={"tag-color"}
                    style={style}
                    onClick={this.onColorClick}
                ></div>
                <div className={"tag-item-block-2"}>
                    {this.props.tag && (
                        <div
                            ref={this.itemRef}
                            className={this.getItemClassName()}
                            style={style}
                        >
                            <div
                                className={"tag-content pr-2"}
                                onDoubleClick={this.onNameDoubleClick}
                                onClick={this.onNameClick}
                            >
                                {this.getTagContent()}
                            </div>
                            {this.state.isLocked && <div></div>}
                        </div>
                    )}
                    {this.renderTagDetail()}
                </div>
            </div>
        );
    }

    public getTagNameRef() {
        return this.itemRef;
    }

    private onDropdownClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        const clickedDropDown = true;
        this.props.onClick(this.props.tag, { clickedDropDown });
    };

    private handleContextMenu = (
        e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>
    ) => {
        e.preventDefault();
        const clickedDropDown = true;
        this.props.onClick(this.props.tag, { clickedDropDown });
    };

    private onColorClick = (e: MouseEvent) => {
        e.stopPropagation();

        const ctrlKey = e.ctrlKey || e.metaKey;
        const altKey = e.altKey;
        const clickedColor = true;
        this.props.onClick(this.props.tag, { ctrlKey, altKey, clickedColor });
    };

    private onNameClick = (e: MouseEvent) => {
        e.stopPropagation();

        const ctrlKey = e.ctrlKey || e.metaKey;
        const altKey = e.altKey;

        this.props.onClick(this.props.tag, { ctrlKey, altKey });
    };

    private onNameDoubleClick = (e: MouseEvent) => {
        e.stopPropagation();
        const { labels } = this.props;
        if (labels.length > 0) {
            this.props.onTagDoubleClick(labels[0]);
        }
    };

    private getItemClassName = () => {
        const classNames = ["tag-item"];
        if (this.props.isSelected) {
            classNames.push("tag-item-selected");
        }
        if (this.props.appliedToSelectedRegions) {
            classNames.push("tag-item-applied");
        }
        return classNames.join(" ");
    };

    private getTagContent = () => {
        const displayIndex = this.getDisplayIndex();
        let confidence = _.get(this.props, "labels[0].confidence", null);
        if (confidence > 0.995) {
            confidence = 0.995;
        }
        let confidencePercent = confidence ? (confidence * 100).toFixed(2) : null;
        return (
            <div
                className={"tag-name-container"}
                onContextMenu={this.handleContextMenu}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}                
            >
                {this.isTypeOrFormatSpecified() && (
                    <FontIcon iconName="Link" className="pl-1" />
                )}
                {!this.state.isRenaming && !this.props.widgetMode && (
                    <div className={"tag-icons-container"}>
                        <span
                            className="tag-index-span"
                            style={{ marginLeft: "7px" }}
                        >
                            <Checkbox
                                title="Delete Tag"
                                className="content-center"
                                checked={this.props.tag.checked}
                                onChange={(e: any, value: boolean) =>
                                    this.onCheckChanged(
                                        this.props.tag.name,
                                        value
                                    )
                                }
                                theme={getPrimaryGreyTheme()}
                            />
                        </span>
                    </div>
                )}
                <div className="tag-name-body" ref={this.tagBodyRef} 
                    style={{
                        width: this.props.splitPaneWidth
                    }}>
                    {this.state.isRenaming ? (
                        <input
                            ref={this.onInputRef}
                            className={`tag-name-editor ${this.getContentClassName()}`}
                            type="text"
                            defaultValue={this.props.tag.name}
                            onKeyDown={(e) => this.onInputKeyDown(e)}
                            onBlur={this.onInputBlur}
                            autoFocus={true}
                        />
                    ) : (
                        <React.Fragment>
                        <span
                            ref={this.tagNameRef}
                            title={`${this.props.tag.name} ${confidencePercent ? `(${confidencePercent}%)` : ""}`}
                            className={this.getContentClassName()}                            
                        >
                            {this.props.tag.name}
                        </span>
                        <span 
                            ref={this.tagConfidenceRef}
                            style={{ 
                                marginLeft: "auto",
                                marginRight: "10px",
                            }}>
                            {this.props.isV3 && confidence && !this.state.isTagNameOverflowing && (
                                <span
                                    style={{
                                        textAlign: "right",
                                        //fontWeight: "bold", // Optional: Make the confidencePercent bold
                                        color: "#FFFFFF", // Optional: Add a blue color for emphasis
                                    }}
                                    title="Confidence Rating"
                                >
                                    {confidencePercent}%
                                </span>
                            )}
                        </span>
                    </React.Fragment>                   
                    )}
                </div>
                {!this.props.widgetMode && 
                    <div className={"tag-icons-container"}>
                        {displayIndex !== null ? (
                            <span className="tag-index-span border border-white rounded-sm ">
                                {displayIndex}
                            </span>
                        ) : (
                            <span className="tag-index-span"></span>
                        )}
                        <IconButton
                            title={strings.tags.toolbar.contextualMenu}
                            ariaLabel={strings.tags.toolbar.contextualMenu}
                            className="tag-input-toolbar-iconbutton ml-2"
                            iconProps={{ iconName: "ChevronDown" }}
                            onClick={this.onDropdownClick}
                        />                                            
                    </div>
                }
            </div>
        );
    };

    private onCheckChanged(name: string, value: boolean) {
        (this.props.tag as any).checked = value;
        // this.props.onDeleteCheckedChanged(name, value);
    }

    private renderTagDetail = () => {
        if (
            this.props.tag.type === FieldType.Object ||
            this.props.tag.type === FieldType.Array
        ) {
            return (
                <div
                    className={"tag-item-label px-2"}
                    onClick={() => {
                        if (this.props.status && this.props.status.toLowerCase() === "completed") {
                            return;
                        }
                        this.props.handleLabelTable(
                            TagInputMode.LabelTable,
                            this.props.tag
                        );
                        this.props.onLabelLeave(this.props.labels[0]);
                    }}
                    onMouseEnter={() =>
                        this.props.onLabelEnter(this.props.labels[0])
                    }
                    onMouseLeave={() =>
                        this.props.onLabelLeave(this.props.labels[0])
                    }
                >
                    <FontIcon className="pr-1 pl-1" iconName="Table" />
                    Click to assign labels
                </div>
            );
        } else {
            let confidence = _.get(this.props, "labels[0].confidence", null);
            if (confidence > 0.995) {
                confidence = 0.995;
            }            
            const revised = _.get(this.props, "labels[0].revised", false);
            return this.props.labels.map((label, idx) => (
                <Fragment key={idx}>
                    <div className="tag-item-label-container"
                        onClick={this.handleMouseClick}
                    >                        
                        <div className="tag-item-label-container-item2">
                            {this.props.showOriginLabels &&
                                label.originValue && (
                                    <TagInputItemLabel
                                        label={label}
                                        isOrigin={true}
                                        value={label.originValue}
                                        onResetClick={this.props.onResetClick}
                                        prefixText={
                                            strings.tags.preText.autoLabel
                                        }
                                        onLabelEdit={this.props.onLabelEdit}
                                        widgetMode={this.props.widgetMode}
                                        isV3={this.props.isV3}
                                        status={this.props.status}
                                    />
                                )}
                            {(label.originValue?.length > 0 ||
                                label.value?.length > 0) && (
                                <TagInputItemLabel
                                    label={label}
                                    value={label.value}
                                    isOrigin={false}
                                    onLabelEnter={this.props.onLabelEnter}
                                    onLabelLeave={this.props.onLabelLeave}
                                    onLabelEdit={this.props.onLabelEdit}
                                    onResetClick={this.props.onResetClick}
                                    prefixText={
                                        revised
                                            ? strings.tags.preText.revised
                                            : undefined
                                    }
                                    widgetMode={this.props.widgetMode}
                                    isV3={this.props.isV3}
                                    status={this.props.status}
                                />
                            )}
                        </div>
                    </div>
                </Fragment>
            ));
        }
    };

    private onInputRef = (element: HTMLInputElement) => {
        this.inputElement = element;
        if (element) {
            element.select();
        }
    };

    onInputBlur = () => {
        this.onRenameTag();
    };

    private onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            this.inputElement.blur();
        } else if (e.key === "Escape") {
            this.setState({
                isRenaming: false,
            });
        }
    };

    private onRenameTag() {
        if (!this.inputElement) {
            return;
        }
        const tag = this.props.tag;
        const name = this.inputElement.value.trim();
        this.props.onRename(tag, name, () => {
            this.inputElement.value = this.props.tag.name;
            this.setState({
                isRenaming: false,
            });
        });
    }

    private getContentClassName = () => {
        const classNames = ["tag-name-text px-2 pb-1"];
        if (this.isTypeOrFormatSpecified()) {
            classNames.push("tag-name-text-typed");
        }
        //classNames.push("flex-center");
        return classNames.join(" ");
    };

    private getDisplayIndex = () => {
        const index = this.props.index;
        if (index >= 0 && index < tagIndexKeys.length) {
            return tagIndexKeys[index];
        }
        return null;
    };

    private isTypeOrFormatSpecified = () => {
        const { tag } = this.props;
        return (
            (tag.type && tag.type !== FieldType.String) ||
            (tag.format && tag.format !== FieldFormat.NotSpecified)
        );
    };

    private handleMouseEnter = () => {
        const { labels } = this.props;
        if (labels.length > 0) {
            this.props.onLabelEnter(labels[0]);
        }
    };

    private handleMouseClick = (e) => {
        if (this.props.status && this.props.status.toLowerCase() === "completed") {
            return;
        }
        const { labels } = this.props;
        if (labels.length > 0) {
            this.props.onLabelClick(labels[0]);
        }
    };    

    private handleMouseLeave = () => {
        const { labels } = this.props;
        if (labels.length > 0) {
            this.props.onLabelLeave(labels[0]);
        }
    };
}
